// components/OptimizedImage.js
import React, { useState, useRef, useEffect } from 'react';

/**
 * Optimized image component with lazy loading, placeholders, and error handling
 */
const OptimizedImage = ({
  src,
  alt,
  className = '',
  placeholderSrc = null,
  fallbackSrc = '/api/placeholder/400/200',
  lazy = true,
  fetchPriority = 'auto',
  onLoad = null,
  onError = null,
  ...props
}) => {
  const [imageState, setImageState] = useState(lazy ? 'loading' : 'loaded');
  const [currentSrc, setCurrentSrc] = useState(() => {
    // For priority images, use real src if available, otherwise use placeholder
    if (!lazy && src && !src.startsWith('data:')) {
      return src;
    }
    return lazy ? (placeholderSrc || fallbackSrc) : src;
  });
  const [isInView, setIsInView] = useState(!lazy);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Add preload hint for priority images
  useEffect(() => {
    if (!lazy && src && !src.startsWith('data:')) {
      // Add DNS prefetch for external domains
      const url = new URL(src, window.location.href);
      if (url.hostname !== window.location.hostname) {
        const dnsLink = document.createElement('link');
        dnsLink.rel = 'dns-prefetch';
        dnsLink.href = `//${url.hostname}`;
        document.head.appendChild(dnsLink);
      }

      // Add preload hint
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [lazy, src]);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before the image comes into view
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [lazy, isInView]);

  // Load the actual image when in view
  useEffect(() => {
    if (!isInView || !src) return;

    // For priority images, set the src immediately without preloading
    if (!lazy && src && !src.startsWith('data:')) {
      setCurrentSrc(src);
      setImageState('loaded');
      if (onLoad) onLoad();
      return;
    }

    const img = new Image();
    
    img.onload = () => {
      setCurrentSrc(src);
      setImageState('loaded');
      if (onLoad) onLoad();
    };

    img.onerror = () => {
      setCurrentSrc(fallbackSrc);
      setImageState('error');
      if (onError) onError();
    };

    img.src = src;
  }, [isInView, src, fallbackSrc, onLoad, onError, lazy]);

  // Generate placeholder based on image state
  const getPlaceholderStyle = () => {
    if (imageState === 'loading') {
      return {
        background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
        backgroundSize: '200% 100%',
        animation: 'shimmer 1.5s infinite'
      };
    }
    return {};
  };

  return (
    <div className={`relative overflow-hidden ${className}`} ref={imgRef}>
      {/* CSS for shimmer animation */}
      <style jsx>{`
        @keyframes shimmer {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }
      `}</style>

      {/* Main image */}
      <img
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover ${
          !lazy ? '' : 'transition-opacity duration-300'
        } ${imageState === 'loaded' ? 'opacity-100' : 'opacity-75'}`}
        style={imageState === 'loading' ? getPlaceholderStyle() : {}}
        fetchpriority={fetchPriority || (!lazy ? 'high' : 'auto')}
        loading={lazy ? 'lazy' : 'eager'}
        decoding={!lazy ? 'sync' : 'async'}
        {...props}
      />

      {/* Loading overlay - only show for lazy loaded images */}
      {imageState === 'loading' && lazy && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gray-400"></div>
        </div>
      )}

      {/* Error overlay */}
      {imageState === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <div className="text-center text-gray-500">
            <svg className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-xs">Image unavailable</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Specialized brand image component with optimized defaults
 */
export const BrandImage = ({
  src,
  alt,
  type = 'cover', // 'cover' or 'logo'
  className = '',
  priority = false, // Add priority prop for LCP optimization
  fetchPriority = 'auto',
  ...props
}) => {
  const getPlaceholder = () => {
    if (type === 'logo') {
      return '/api/placeholder/200/200';
    }
    return '/api/placeholder/400/200';
  };

  const getFallback = () => {
    if (type === 'logo') {
      return '/api/placeholder/200/200';
    }
    return '/api/placeholder/400/200';
  };

  return (
    <OptimizedImage
      src={src}
      alt={alt}
      className={className}
      placeholderSrc={getPlaceholder()}
      fallbackSrc={getFallback()}
      lazy={false} // Disabled lazy loading - load all images immediately
      fetchPriority={fetchPriority}
      {...props}
    />
  );
};

/**
 * Image preloader utility
 */
export const preloadImages = (imageUrls) => {
  return Promise.all(
    imageUrls.map(url => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(url);
        img.onerror = () => reject(url);
        img.src = url;
      });
    })
  );
};

/**
 * Hook for managing image loading state
 */
export const useImageLoader = (src) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!src) return;

    setLoading(true);
    setError(false);

    const img = new Image();
    
    img.onload = () => {
      setLoading(false);
      setError(false);
    };

    img.onerror = () => {
      setLoading(false);
      setError(true);
    };

    img.src = src;
  }, [src]);

  return { loading, error };
};

export default OptimizedImage;